import { Separator } from "@/components/ui/separator";
import { m } from "@/paraglide/messages";
import { Loader2 } from "lucide-react";
import { type JSX, useCallback, useState } from "react";
import { toast } from "sonner";
import {
	type OrderProduct,
	PaymentChannel,
	createOrder,
	createSubscriptionOrder,
} from "./api";

export default function PaymentMethodSelector({
	selectedProduct,
	setCryptoOpen,
	isConnected,
	openConnectModal,
	setPendingWalletChange,
	supportedChannels,
	orderProducts,
}: {
	selectedProduct: OrderProduct | null;
	setCryptoOpen: (open: boolean) => void;
	isConnected: boolean;
	openConnectModal?: () => void;
	setPendingWalletChange?: (v: boolean) => void;
	supportedChannels?: string[];
	orderProducts?: OrderProduct[];
}) {
	const [stripeLoading, setStripeLoading] = useState(false);

	const handleCryptoClick = () => {
		if (selectedProduct) {
			if (!isConnected) {
				setPendingWalletChange?.(true);
				openConnectModal?.();
			} else {
				setCryptoOpen(true);
			}
		}
	};

	const handleSegpayClick = () => {
		if (selectedProduct) {
			const merchantid = "123456";
			const packageid = "123456";
			const form = document.createElement("form");
			form.method = "POST";
			form.action = "https://secure.segpay.com/billing/signup"; // 或你专属的集成地址

			const fields = {
				merchantid,
				currency: selectedProduct.currency_name || "USD",
				packageid,
				price: selectedProduct.price,
				successURL: `${window.location.href}?deposit_fullback=true&deposit_success=true`,
				failURL: `${window.location.href}?deposit_fullback=true&deposit_success=false`,
			};

			for (const [key, value] of Object.entries(fields)) {
				const input = document.createElement("input");
				input.type = "hidden";
				input.name = key;
				input.value = value as string;
				form.appendChild(input);
			}

			document.body.appendChild(form);
			form.submit();
		}
	};

	const handleStripeClick = useCallback(async () => {
		if (!selectedProduct) return;
		setStripeLoading(true);
		const sendParams = {
			product_id: selectedProduct?.id,
			payment_channel: PaymentChannel.Stripe,
			stripe_success_url: `${window.location.href}?deposit_fullback=true&deposit_success=true`,
			stripe_cancel_url: `${window.location.href}?deposit_fullback=true&deposit_success=false`,
		};
		const { data, msg } =
			selectedProduct.type === "Once"
				? await createOrder(sendParams)
				: await createSubscriptionOrder(sendParams);
		if (data.stripe_order_details?.pay_url) {
			sessionStorage.setItem("deposit_auto_open", "1");
			sessionStorage.setItem("orderProducts", JSON.stringify(orderProducts));
			window.location.href = data.stripe_order_details.pay_url;
		} else {
			toast.error(msg);
		}
		setStripeLoading(false);
	}, [selectedProduct]);

	const paymentMethodComponents: { [key: string]: JSX.Element } = {
		PaymentChannelStripe: (
			<div
				className={`flex flex-col w-[155px] items-center cursor-pointer relative ${
					!selectedProduct ? "pointer-events-none" : ""
				}`}
				onClick={handleStripeClick}
			>
				{stripeLoading && (
					<div className="absolute inset-0 bg-black/20 flex items-center justify-center rounded-[20px] z-10">
						<Loader2 className="animate-spin h-8 w-8 text-white" />
					</div>
				)}
				<div className="flex gap-10 items-center h-8">
					<img
						src={`/assets/images/deposit/card_pay${
							!selectedProduct ? "_unavailable" : ""
						}.svg`}
						alt="master_card"
					/>
				</div>
				<span className="text-sm font-medium text-t2 mt-2">
					{m.credit_card_payment()}
				</span>
			</div>
		),
		PaymentChannelWeb3: (
			<div
				className={`flex flex-col w-[155px] items-center cursor-pointer ${
					!selectedProduct || selectedProduct?.type === "Subscription"
						? "pointer-events-none opacity-50"
						: ""
				}`}
				onClick={
					selectedProduct?.type === "Subscription"
						? undefined
						: handleCryptoClick
				}
			>
				<div className="flex gap-10 items-center h-8">
					<img
						src={`/assets/images/deposit/web3_pay${
							!selectedProduct || selectedProduct?.type === "Subscription"
								? "_unavailable"
								: ""
						}.svg`}
						alt="web3_pay"
					/>
				</div>
				<span className="text-sm font-medium text-t2 mt-2">
					{m.crypto_payment()}
				</span>
			</div>
		),
		PaymentChannelSegpay: (
			<div
				className={`flex flex-col w-[155px] items-center cursor-pointer ${
					!selectedProduct ? "pointer-events-none" : ""
				}`}
				onClick={handleSegpayClick}
			>
				<div className="flex gap-10 items-center h-8">
					<img
						src={`/assets/images/deposit/segpay_pay${
							!selectedProduct ? "_unavailable" : ""
						}.svg`}
						alt="segpay_pay"
					/>
				</div>
				<span className="text-sm font-medium text-t2 mt-2">
					{m.segpay_payment()}
				</span>
			</div>
		),
	};

	// 排序：先 PaymentChannelStripe，再 PaymentChannelWeb3，其他排后
	const paymentOrder = [
		"PaymentChannelStripe",
		"PaymentChannelWeb3",
		"PaymentChannelSegpay",
	];
	const sortedChannels = supportedChannels
		? [...supportedChannels].sort((a, b) => {
				const ia = paymentOrder.indexOf(a);
				const ib = paymentOrder.indexOf(b);
				if (ia === -1 && ib === -1) return 0;
				if (ia === -1) return 1;
				if (ib === -1) return -1;
				return ia - ib;
			})
		: [];
	const channels = sortedChannels.map(
		(channel) => paymentMethodComponents[channel],
	);

	return (
		<div className="mb-4">
			<div className="font-medium mb-4 text-base text-t2 px-2 h-[32px] inline-flex items-center justify-center shadow-card rounded-[20px] bg-card">
				{m.profile_deposit_channel_payment_method()}
			</div>
			<div className="flex items-center px-4 md:px-8 justify-center">
				{channels?.map((ChannelComponent, index) => (
					<div key={index} className="contents">
						{ChannelComponent}
						{index < channels.length - 1 && (
							<Separator
								orientation="vertical"
								className="!h-[67px] mx-[73px] hidden md:block"
							/>
						)}
					</div>
				))}
			</div>
		</div>
	);
}
